import { CheckCircleOutline, DraftsOutlined, ErrorOutline, PeopleOutline } from '@mui/icons-material';
import { Box, Button, CircularProgress, Grid, Stack, Tooltip, Typography, lighten } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceMailStatsResponse, InvoiceMailStatusFilter, getInvoiceMailStatsByMission } from 'src/shared/services/emailService';
import { InvoiceMailDeliveryStatusDataGrid } from './invoiceMailDeliveryStatusDataGrid';

type InvoiceMailDeliveryStatusModalProps = {
  modalOpen: boolean;
  missionId: number;
  missionName?: string;
  sendingType: InvoiceMailStatusFilter['sendingType'];
  onClose: () => void;
};

type StatCardProps = {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  iconColor?: 'info' | 'success' | 'error';
};

const StatCard = ({ icon, label, children, iconColor = 'info' }: StatCardProps) => (
  <Box sx={{ flex: '0 0 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: (thm) => lighten(thm.palette[iconColor].main, 0.85),
        color: `${iconColor}.main`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='body2' sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
        {label}
      </Typography>
      {children}
    </Box>
  </Box>
);

export const InvoiceMailDeliveryStatusModal = (props: InvoiceMailDeliveryStatusModalProps) => {
  const { modalOpen, missionId, missionName, sendingType, onClose } = props;
  const { t } = useTranslation(['common', 'groupMessage', 'invoice']);
  const isMounted = useIsMounted();
  const [stats, setStats] = useState<InvoiceMailStatsResponse | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getInvoiceMailStatsByMission(missionId, sendingType);
      if (isMounted()) setStats(res.data);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    }
  }, [missionId, sendingType, isMounted]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const total = stats?.total ?? 0;
  const sent = stats?.sent ?? 0;
  const opened = stats?.opened ?? 0;
  const errorCount = stats?.error ?? 0;
  const openedPct = sent > 0 ? Math.round((opened / sent) * 100) : 0;

  let openedRingColor = 'error.main';
  if (openedPct >= 40) openedRingColor = 'success.main';
  else if (openedPct >= 20) openedRingColor = 'warning.main';

  const lastSentLabel = stats?.lastSentAt && dayjs(stats.lastSentAt).isValid() ? dayjs(stats.lastSentAt).format('YYYY-MM-DD HH:mm') : '—';

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('groupMessage:sms.deliveryStatus') as string}
      dialogSubTitle={
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {missionName} — {sendingType}
        </Typography>
      }
      onClose={onClose}
      onSave={onClose}
      dialogContent={
        <Box className='section-inset' sx={{ p: 2 }}>
          <GridWrapper>
            <Grid item xs={12}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 6 }} sx={{ minWidth: 0, py: 1 }}>
                <StatCard icon={<CheckCircleOutline />} label={t('groupMessage:sms.sent') as string} iconColor='success'>
                  <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                    {lastSentLabel}
                  </Typography>
                </StatCard>
                <StatCard icon={<PeopleOutline />} label='Antal mottagare'>
                  <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {sent}/{total}
                  </Typography>
                </StatCard>
                <Box sx={{ flex: '0 0 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Tooltip title={`${opened} av ${sent} öppnade (${openedPct}%)`} arrow>
                    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CircularProgress variant='determinate' value={100} size={44} thickness={4} sx={{ color: 'grey.300', position: 'absolute' }} />
                      <CircularProgress variant='determinate' value={openedPct} size={44} thickness={4} sx={{ color: openedRingColor }} />
                      <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DraftsOutlined sx={{ fontSize: '1.1rem', color: openedRingColor }} />
                      </Box>
                    </Box>
                  </Tooltip>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant='body2' sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
                      Öppningsgrad
                    </Typography>
                    <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {openedPct}%{' '}
                      <Typography component='span' variant='body2' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        ({opened}/{sent})
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
                {errorCount > 0 && (
                  <StatCard icon={<ErrorOutline />} label='Fel' iconColor='error'>
                    <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {errorCount} st
                    </Typography>
                  </StatCard>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <InvoiceMailDeliveryStatusDataGrid missionId={missionId} sendingType={sendingType} />
            </Grid>
          </GridWrapper>
        </Box>
      }
      dialogActions={
        <Button variant='outlined' disableElevation onClick={onClose}>
          {t('common:button.cancel') as string}
        </Button>
      }
    />
  );
};
